// =============================================================================
// sprites.ts (src/client/presentation) — Pixel art procedural: todos os
// personagens, inimigos, bosses e projéteis desenhados pixel a pixel (zero
// assets externos, zero emoji). Versão TypeScript do antigo client/js/sprites.js.
//
// Formato: grade de strings onde cada letra indexa uma cor da paleta ('.' =
// transparente). Humanoides compartilham o template PERSON e se diferenciam
// por paleta + "overlays" (retângulos desenhados por cima: faixa presidencial,
// óculos, boina, capacete...).
//
// PIXI é um global (carregado via <script> em /vendor); os tipos vêm de
// globals.d.ts — o import abaixo é type-only e some no bundle.
// =============================================================================

import type { Texture } from 'pixi.js';
import { DEFAULT_SKIN } from '../../shared/skins';

// Overlay: retângulo desenhado por cima da grade [x, y, w, h, cor]
export type Overlay = [x: number, y: number, w: number, h: number, cor: string];

export interface SpriteDef {
  p: Record<string, string>;
  rows: string[];
  ov: Overlay[];
}

// Paleta base compartilhada (tons de pele, contorno, etc.)
const K = '#14141a';    // contorno
const SKIN = '#e8b48a';
const SKIN2 = '#d99f74'; // sombra de pele
const WHITE = '#f4f4f4';
const EYE = '#1d1d26';

// ---------------------------------------------------------------------------
// Template humanoide 16x18 (busto chibi): h=cabelo/chapéu, s=pele, d=sombra,
// e=olho, m=boca, b=camisa, c=detalhe da camisa, k=contorno
// ---------------------------------------------------------------------------
const PERSON = [
  '.....hhhhhh.....',
  '...hhhhhhhhhh...',
  '..hhhhhhhhhhhh..',
  '..hhhhhhhhhhhh..',
  '..hhssssssssdh..',
  '..hssssssssssd..',
  '..sseesssseess..',
  '..ssssssssssss..',
  '..sdsssmmsssds..',
  '...ssssmmssss...',
  '....ssssssss....',
  '..kbbbbbbbbbbk..',
  '.kbbbbbbbbbbbbk.',
  '.kbbccbbbbccbbk.',
  'ksbbbbbbbbbbbbsk',
  'ksbbbbbbbbbbbbsk',
  '.kbbbbbbbbbbbbk.',
  '..kkkk....kkkk..',
];

// Paleta padrão do PERSON (cada def sobrescreve o que quiser)
const PBASE: Record<string, string> = {
  k: K, s: SKIN, d: SKIN2, e: EYE, m: '#a05a48',
  h: '#3a2a1e', b: '#5a5a66', c: '#5a5a66',
};

// def humanoide: {p: paleta extra, ov: overlays [x, y, w, h, cor]}
const person = (p: Record<string, string> = {}, ov: Overlay[] = []): SpriteDef =>
  ({ rows: PERSON, p: { ...PBASE, ...p }, ov });

// santinho de candidato jogado no chão (lixo eleitoral oficial) — muda só a
// cor do partido; o rosto é genérico porque candidato é tudo igual mesmo
const santinho = (cor: string): SpriteDef => ({
  p: { w: '#c4c4bc', h: '#2a2a30', s: '#e8b48a', k: '#20202a', r: cor },
  rows: [
    'wwwwwwwwww',
    'wwwhhhhwww',
    'wwhhsshhww',
    'wwwsksksww',
    'wwwsssswww',
    'wwwwsswwww',
    'wrrrrrrrrw',
    'wrwkkwkkrw',
    'wrrrrrrrrw',
    'wwwwwwwwww',
  ],
  ov: [],
});

// ---------------------------------------------------------------------------
// Registro de sprites
// ---------------------------------------------------------------------------
export const SPRITES = {
  // ======================= JOGADORES =======================
  bolsonaro: person(
    { h: '#23222a', b: '#20304a' }, // topete escuro, terno azul-marinho
    [
      // faixa presidencial diagonal (ombro direito -> quadril esquerdo)
      [3, 11, 3, 1, '#1e9e4e'], [4, 12, 3, 1, '#f7d117'], [5, 13, 3, 1, '#1e9e4e'],
      [6, 14, 3, 1, '#f7d117'], [7, 15, 3, 1, '#1e9e4e'], [8, 16, 3, 1, '#f7d117'],
    ]),
  lula: person(
    { h: '#9c9c9c', b: '#c1272d', c: '#f4f4f4' }, // grisalho, camisa vermelha
    [
      // barba grisalha contornando o rosto
      [2, 7, 2, 3, '#b5b5b5'], [12, 7, 2, 3, '#b5b5b5'],
      [3, 9, 3, 2, '#b5b5b5'], [10, 9, 3, 2, '#b5b5b5'],
      [4, 10, 8, 1, '#b5b5b5'],
      [7, 13, 2, 2, '#f4f4f4'], // estrelinha no peito
      [4, 0, 8, 2, '#8c8c8c'],
    ]),
  dilma: person(
    { h: '#5a3a26', b: '#b03030', c: '#7a1f1f' }, // chanel castanho, blazer
    [
      [1, 4, 2, 6, '#5a3a26'], [13, 4, 2, 6, '#5a3a26'], // cabelo chanel nas laterais
      [2, 10, 2, 1, '#5a3a26'], [12, 10, 2, 1, '#5a3a26'],
      [6, 11, 4, 2, '#f4f4f4'], // colar/gola clara
    ]),
  datena: person(
    { h: '#c9c9c9', b: '#20304a', c: '#f4f4f4' }, // grisalho de plantão, terno azul
    [
      [4, 6, 2, 1, '#8a8a8a'], [10, 6, 2, 1, '#8a8a8a'],  // sobrancelha indignada
      [7, 12, 2, 4, '#7a1f1f'],                           // gravata vinho de bancada
      [12, 12, 4, 3, '#8a5a2a'], [13, 11, 2, 1, '#8a5a2a'], // cadeira de prontidão na mão
    ]),
  marinasilva: person(
    { h: '#4a4a4a', b: '#7a1f1f', c: '#5a1114' }, // coque grisalho, blusa vinho
    [
      [3, 6, 4, 2, '#0f0f14'], [9, 6, 4, 2, '#0f0f14'],   // armação dos óculos
      [4, 6, 2, 1, '#cfe8ff'], [10, 6, 2, 1, '#cfe8ff'],  // lentes
      [7, 7, 2, 1, '#0f0f14'],                            // ponte
      [6, 11, 4, 1, '#d4af37'],                           // colar dourado discreto
    ]),
  pablomarcal: person(
    { h: '#1a1a20', b: '#1c2b45', c: '#1c2b45' }, // topete de coach, blazer azul
    [
      [4, 6, 2, 1, '#0f0f14'], [10, 6, 2, 1, '#0f0f14'],  // olhar de close motivacional
      [6, 11, 4, 3, '#f4f4f4'],                           // camisa branca sem gravata
      [13, 12, 3, 3, '#2b2b33'], [14, 11, 2, 2, '#4a90d9'], // celular gravando corte
    ]),
  padrekelmon: person(
    { h: '#23232a', b: '#191920', c: '#191920' }, // touca e batina pretas
    [
      [5, 1, 1, 1, '#f4f4f4'], [8, 0, 1, 1, '#f4f4f4'], [11, 2, 1, 1, '#f4f4f4'], // cruzinhas da touca
      [2, 7, 2, 3, '#b5b5b5'], [12, 7, 2, 3, '#b5b5b5'],  // barba grisalha
      [3, 9, 3, 2, '#b5b5b5'], [10, 9, 3, 2, '#b5b5b5'],
      [4, 10, 8, 1, '#b5b5b5'],
      [7, 12, 2, 3, '#d4af37'], [6, 13, 4, 1, '#d4af37'], // cruz dourada no peito
    ]),
  temer: person(
    { h: '#d9d9d9', b: '#23232b', c: '#1a1a22' }, // topete branco, terno de posse
    [
      [4, 6, 2, 1, '#8a8a8a'], [10, 6, 2, 1, '#8a8a8a'],  // sobrancelha solene
      [6, 9, 1, 1, '#f4f4f4'], [9, 9, 1, 1, '#f4f4f4'],   // presas discretas
      [7, 12, 2, 4, '#7a1f1f'],                           // gravata vinho
    ]),

  // ------------------- SKINS (issue #24): variantes '<charId>_<skinId>' -----
  lula_papuda: person(
    { h: '#9c9c9c', b: '#e07020', c: '#e07020' }, // grisalho, macacão laranja
    [
      // barba grisalha contornando o rosto (igual ao traje oficial)
      [2, 7, 2, 3, '#b5b5b5'], [12, 7, 2, 3, '#b5b5b5'],
      [3, 9, 3, 2, '#b5b5b5'], [10, 9, 3, 2, '#b5b5b5'],
      [4, 10, 8, 1, '#b5b5b5'],
      [4, 0, 8, 2, '#8c8c8c'],
      // listras do uniforme + tarja com o número de registro
      [3, 12, 10, 1, '#b05518'], [2, 14, 12, 1, '#b05518'], [3, 16, 10, 1, '#b05518'],
      [6, 13, 4, 1, '#f4e6d0'],
    ]),
  bolsonaro_papuda: person(
    { h: '#23222a', b: '#e07020', c: '#e07020' }, // topete escuro, macacão laranja
    [
      // listras do uniforme + tarja com o número de registro (sem faixa)
      [3, 12, 10, 1, '#b05518'], [2, 14, 12, 1, '#b05518'], [3, 16, 10, 1, '#b05518'],
      [6, 13, 4, 1, '#f4e6d0'],
    ]),
  temer_conde: person(
    { h: '#d9d9d9', s: '#cfd4e6', d: '#aab2cc', b: '#1d1430', c: '#150e24' }, // palidez de cripta, capa púrpura
    [
      [4, 6, 2, 1, '#8a8a8a'], [10, 6, 2, 1, '#8a8a8a'],  // sobrancelha solene
      [6, 9, 1, 2, '#f4f4f4'], [9, 9, 1, 2, '#f4f4f4'],   // presas de reforma da previdência
      [1, 10, 2, 3, '#3a1a4f'], [13, 10, 2, 3, '#3a1a4f'], // gola alta da capa
      [7, 12, 2, 4, '#8f1d22'],                            // gravata cor de sangue
    ]),

  // ======================= INIMIGOS =======================
  urna_possuida: {
    // urna eletrônica possuída (fallback procedural do atlas em /assets/fx):
    // caixa branca, tela verde com olhos malignos, teclado e perninhas
    p: { k: K, w: '#e8e8ec', d: '#c2c2ca', g: '#b8e0a0', e: '#1d3a14', o: '#e07020', b: '#3a3a44' },
    rows: [
      '.......kk.......',
      '..kkkkkkkkkkkk..',
      '.kwwwwwwwwwwwwk.',
      '.kwkkkkkkkkkkwk.',
      '.kwkggggggggkwk.',
      '.kwkgeegeegekwk.',
      '.kwkggeggeggkwk.',
      '.kwkkkkkkkkkkwk.',
      '.kwbbwbbwbbwwwk.',
      '.kwbbwbbwbbwowk.',
      '.kwbbwbbwbbwwwk.',
      '.kwddddddddddwk.',
      '..kkkkkkkkkkkk..',
      '...kbk....kbk...',
      '...kkk....kkk...',
    ],
    ov: [],
  },
  santinho_voador: {
    // santinho mal-assombrado: panfleto de campanha com sorriso maligno,
    // borda inferior rasgada esvoaçando
    p: { k: K, w: '#ece5cf', r: '#c1272d', e: '#d42a2a', m: '#5a1a1a', t: '#f4f4f4' },
    rows: [
      '..kkkkkkkk..',
      '.kwwwwwwwwk.',
      '.kwrrrrrrwk.',
      '.kwwwwwwwwk.',
      '.kwekwwkewk.',
      '.kweewweewk.',
      '.kwwwwwwwwk.',
      '.kwmmmmmmwk.',
      '.kwmtmtmtwk.',
      '.kwwmmmmwwk.',
      '.kwwwwwwwwk.',
      '..kwwkkwwk..',
      '...kk..kk...',
    ],
    ov: [],
  },
  titulo_zumbi: {
    // título de eleitor zumbi: documento bege rasgado, olhos verdes mortos,
    // boca costurada e mãozinhas podres saindo das laterais
    p: { k: K, b: '#d8cfae', d: '#a89a72', e: '#9fdc5a', m: '#4a3527', g: '#7aa05a' },
    rows: [
      '..kkkk..kkkk..',
      '.kbbbbkkbbbbk.',
      '.kbddddddddbk.',
      '.kbbbbbbbbbbk.',
      '.kbeebbbbeebk.',
      '.kbbebbbbebbk.',
      '.kbbbbbbbbbbk.',
      'kkbbmmmmmmbbkk',
      'kgkbmbmbmbbkgk',
      'kkkbbbbbbbbkkk',
      '.kbddddddddbk.',
      '.kbbbbbbbbbbk.',
      '..kkkk.kkkkk..',
    ],
    ov: [],
  },
  biometria_fantasma: {
    // mão espectral apontando pra cima com digital brilhando no indicador,
    // desmanchando em fiapos de ectoplasma embaixo
    p: { k: '#3a4a66', w: '#dce8f4', b: '#a8c4e6', g: '#7ff0ff', G: '#38b8e0' },
    rows: [
      '.....kk........',
      '....kggk.......',
      '....kgGk.......',
      '....kwwk.......',
      '....kwwkkkk....',
      '....kwwkwwwk...',
      '...kwwwwwwwwk..',
      '..kwwwwwwwwwwk.',
      '..kwbwwwwwwbwk.',
      '..kwwwwwwwwwwk.',
      '...kwwwwwwwwk..',
      '...kbwwwwwwbk..',
      '....kwwwwwwk...',
      '.....ww..ww....',
      '.....w....w....',
    ],
    ov: [],
  },
  robo_fake_news: {
    // robô de fake news: cabeça de monitor com balão de zap na tela,
    // antena piscando e corpinho de lata
    p: { k: K, o: '#ff4433', s: '#9aa0a6', d: '#6b7076', c: '#12281c', g: '#25d366', e: '#d42a2a', w: WHITE },
    rows: [
      '........k.......',
      '.......kok......',
      '........k.......',
      '..kkkkkkkkkkkk..',
      '.kssssssssssssk.',
      '.kskkkkkkkkkksk.',
      '.kskccccccccksk.',
      '.kskcggggggcksk.',
      '.kskcgeggegcksk.',
      '.kskcggwwggcksk.',
      '.kskccgcccccksk.',
      '.kskkkkkkkkkksk.',
      '..kkkkkkkkkkkk..',
      '..ksk.ksssk.ksk.',
      '..kkk.kdddk.kkk.',
      '......kk.kk.....',
    ],
    ov: [],
  },
  carro_de_som: {
    // carro de som de campanha: corneta no teto, faixa vermelha e faróis
    // acesos de raiva com grade arreganhada
    p: { k: K, s: '#8a8f98', w: '#f0f0f0', g: '#26323e', r: '#c1272d', y: '#ffd23a', d: '#3a3a44', t: '#2b2b33' },
    rows: [
      '......kkkk........',
      '.....kssssk.......',
      '......kssk........',
      '.......kk.........',
      '..kkkkkkkkkkkkkk..',
      '.kwwwwwwwwwwwwwwk.',
      '.kwkggggggggggkwk.',
      '.kwkggggggggggkwk.',
      '.kwwwwwwwwwwwwwwk.',
      '.kwrrrrrrrrrrrrwk.',
      '.kwwwwwwwwwwwwwwk.',
      '.kyykwwwwwwwwkyyk.',
      '.kyykwwwwwwwwkyyk.',
      '.kwwkddkddkddkwwk.',
      'kkkkkkkkkkkkkkkkkk',
      '..ktk........ktk..',
      '..kkk........kkk..',
    ],
    ov: [],
  },
  mala_de_dinheiro: {
    // mala de dinheiro viva: notas saindo pela costura (que faz as vezes de
    // boca), olhos gananciosos dourados e perninhas atarracadas
    p: { k: K, m: '#7a5230', d: '#5a3c22', g: '#2ecc71', G: '#1a9e4a', y: '#f7d117', w: WHITE },
    rows: [
      '....gg....gg.....',
      '...kGgk..kGgk....',
      '......kkkkk......',
      '.....kd...dk.....',
      '.kkkkkkkkkkkkkkk.',
      'kmmmmmmmmmmmmmmmk',
      'kmwwwwmmmmmwwwwmk',
      'kmwyywmmmmmwyywmk',
      'kdgGgdgGgdgGgdgdk',
      'kmmmmmkyyykmmmmmk',
      'kmmmmmmmmmmmmmmmk',
      'kmdddddddddddddmk',
      '.kkkkkkkkkkkkkkk.',
      '...kmk.....kmk...',
      '...kkk.....kkk...',
    ],
    ov: [],
  },
  pesquisa_maldita: {
    // pesquisa maldita: gráfico de barras emoldurado onde as barras formam
    // um rosto maligno, com raio de margem de erro rachando o vidro
    p: { k: K, f: '#5a4632', w: '#f0ead6', b: '#3a5a8f', e: '#d42a2a', r: '#c1272d', y: '#f7d117', d: '#8a8f98' },
    rows: [
      '.kkkkkkkkkkkkkk.',
      'kffffffffffffffk',
      'kfkkkkkkkkkkkkfk',
      'kfkwwwwwwwwwykfk',
      'kfkwwwwwwwwywkfk',
      'kfkwbbwwwbbwykfk',
      'kfkwbewwwbewwkfk',
      'kfkwbbwwwbbwwkfk',
      'kfkwbbwbwbbwwkfk',
      'kfkwrrrrrrrrwkfk',
      'kfkwrwrrwrrwwkfk',
      'kfkddddddddddkfk',
      'kfkkkkkkkkkkkkfk',
      'kffffffffffffffk',
      '.kkkkkkkkkkkkkk.',
    ],
    ov: [],
  },
  drone_fiscal: {
    // drone fiscalizador: quadricóptero achatado com hélices borradas,
    // olho de lente vermelha e luzinha de gravação
    p: { k: K, r: '#c9cdd6', d: '#5a5f66', s: '#9aa0a6', o: '#ff4433', c: '#1d1d26', e: '#d42a2a' },
    rows: [
      'rrrrr......rrrrr',
      '..kdk......kdk..',
      '...kdkkkkkkdk...',
      '..ksssssssssok..',
      '.kskkkkkkkkkksk.',
      '.kskccceecccksk.',
      '.kskkkkkkkkkksk.',
      '..kddddddddddk..',
      '...kk......kk...',
      '..kdddk..kdddk..',
    ],
    ov: [],
  },
  trio_eletrico: {
    // trio elétrico assombrado: paredão de caixas de som em cima, cabine com
    // faróis furiosos e grade dentuça (inimigo grandão, escala de boss)
    p: { k: K, s: '#3a3a44', S: '#6b7076', r: '#c1272d', g: '#26323e', y: '#ffd23a', d: '#1d1d26', w: '#f4f4f4', t: '#2b2b33' },
    rows: [
      '.kkkkkkkkkkkkkkkkkkkkkk.',
      '.kssssssssssssssssssssk.',
      '.ksSSssSSssSSssSSssSSsk.',
      '.ksSSssSSssSSssSSssSSsk.',
      '.kssssssssssssssssssssk.',
      '.ksSSssSSssSSssSSssSSsk.',
      '.ksSSssSSssSSssSSssSSsk.',
      '.kssssssssssssssssssssk.',
      '.kkkkkkkkkkkkkkkkkkkkkk.',
      '...kkkkkkkkkkkkkkkkkk...',
      '..krrrrrrrrrrrrrrrrrrk..',
      '..krkkkkkkkkkkkkkkkkrk..',
      '..krkggggggggggggggkrk..',
      '..krkggggggggggggggkrk..',
      '..krrrrrrrrrrrrrrrrrrk..',
      '..kryykrrrrrrrrrrkyyrk..',
      '..kryykrrrrrrrrrrkyyrk..',
      '..krkddddddddddddddkrk..',
      '..krkdwdwdwdwdwdwdwkrk..',
      '..kkkkkkkkkkkkkkkkkkkk..',
      '..kttk............kttk..',
      '..kkkk............kkkk..',
    ],
    ov: [],
  },
  // (sprite de ALIADO: invocado pelo PIX das Emendas, não é inimigo)
  corrupto: person(
    { h: '#3a2a1e', b: '#2b2b33', c: '#1a1a22' },
    [
      [7, 12, 2, 4, '#1e9e4e'],                            // gravata de dinheiro
      [6, 13, 1, 1, '#f7d117'], [9, 14, 1, 1, '#f7d117'],  // cifrões caindo
      [3, 5, 10, 1, '#0f0f14'],                            // sombra suspeita nos olhos
    ]),

  // ======================= BOSSES (24 px, objetos icônicos) =======================
  boss_live_19h: {
    // TV de tubo transmitindo a live: tela verde com rosto bravo
    p: { k: K, t: '#5a4632', d: '#3f3123', g: '#2fdd6f', G: '#1a9e4a', w: WHITE, r: '#ff4433', a: '#9aa0a6' },
    rows: [
      '......a........a........',
      '.......a......a.........',
      '........a....a..........',
      '..kkkkkkkkkkkkkkkkkkkk..',
      '.kttttttttttttttttttttk.',
      '.ktkkkkkkkkkkkkkkkkkttk.',
      '.ktkggggggggggggggggktk.',
      '.ktkgGGgggggggggGGggktk.',
      '.ktkggGGgggggggGGgggktk.',
      '.ktkgggwwggggggwwgggktk.',
      '.ktkgggwGggggggwGgggktk.',
      '.ktkggggggggggggggggktk.',
      '.ktkggggGGGGGGGGggggktk.',
      '.ktkgggGGggggggGGgggktk.',
      '.ktkggggggggggggggggktk.',
      '.ktkkkkkkkkkkkkkkkkkttk.',
      '.kttttttttttttttrtttttk.',
      '.ktttttttttttttttttttdk.',
      '..kkkkkkkkkkkkkkkkkkkk..',
      '....kdk..........kdk....',
      '....kkk..........kkk....',
    ],
    ov: [],
  },
  boss_mensalao2: {
    // mala de dinheiro transbordando propina
    p: { k: K, m: '#7a5230', d: '#5a3c22', g: '#2ecc71', G: '#1a9e4a', y: '#f7d117', w: WHITE },
    rows: [
      '.....ggg....ggg.........',
      '....gGGGg..gGGGg..ggg...',
      '....gGwGg..gGwGg.gGGGg..',
      '.....ggg....ggg..gGwGg..',
      '..........kkkk....ggg...',
      '.........kddddk.........',
      '..kkkkkkkkkkkkkkkkkkkk..',
      '.kmmmmmmmmmmmmmmmmmmmmk.',
      '.kmddddddddddddddddddmk.',
      '.kmmmmmmmmkkkkmmmmmmmmk.',
      '.kmmmmmmmkyyyykmmmmmmmk.',
      '.kmmmmmmmkyyyykmmmmmmmk.',
      '.kmmmmmmmmkkkkmmmmmmmmk.',
      '.kmmmmmmmmmmmmmmmmmmmmk.',
      '.kmdddddddddddddddddmmk.',
      '.kmmmmmmmmmmmmmmmmmmmmk.',
      '..kkkkkkkkkkkkkkkkkkkk..',
    ],
    ov: [],
  },
  boss_impeachment_dilma: {
    // panela gigante do panelaço com colher de pau
    p: { k: K, p: '#4a4a56', d: '#33333d', s: '#8a8f98', w: '#b0692f', W: '#8a4f22', r: '#c1272d' },
    rows: [
      '..................ww....',
      '.................wWw....',
      '................wWw.....',
      '...............wWw......',
      '..............wWw.......',
      '.............kkk........',
      '....kkkkkkkkkkkkkkkk....',
      '...kppppppppppppppppk...',
      '..kssssssssssssssssssk..',
      '..kppppppppppppppppppk..',
      'kkkpddppppppppppppddpkkk',
      'ksskpppppppppppppppppkssk',
      'kkkppppprrrrrrrrpppppkkk',
      '..kpppppprrrrrrppppppk..',
      '..kpddpppppppppppppdpk..',
      '..kppppppppppppppppppk..',
      '...kkkkkkkkkkkkkkkkkk...',
    ],
    ov: [],
  },
  boss_cpi_covid: {
    // circo pegando fogo (metaforicamente)
    p: { k: K, r: '#c1272d', w: '#f0e6d2', d: '#8f1d22', y: '#f7d117', m: '#5a4632' },
    rows: [
      '...........y............',
      '..........yy............',
      '...........k............',
      '..........kkk...........',
      '.........krwrk..........',
      '........krrwrrk.........',
      '.......krrwwwrrk........',
      '......krrrwwwrrrk.......',
      '.....krrrwwrwwrrrk......',
      '....krrrwwrrrwwrrrk.....',
      '...krrrwwrrrrrwwrrrk....',
      '..krrrwwrrrrrrrwwrrrk...',
      '..kwwwwwwwwwwwwwwwwwk...',
      '..kwrrwwrrwwwrrwwrrwk...',
      '..kwrrwwrrwdwrrwwrrwk...',
      '..kwrrwwrrwddwrwwrrwk...',
      '..kwrrwwrrwdddwwwrrwk...',
      '...kkkkkkkkkkkkkkkkk....',
    ],
    ov: [],
  },
  boss_stf_bolso: {
    // martelo monocrático + balança dourada
    p: { k: K, m: '#5a3c22', d: '#3f2a18', g: '#d4af37', G: '#a8862a', w: '#f0e6d2' },
    rows: [
      '......kkkkkkkk..........',
      '.....kmmmmmmmmk.........',
      '.....kmddmmddmk.........',
      '.....kmmmmmmmmk.........',
      '......kkkmmkkk..........',
      '.........kmk............',
      '.........kmk......gg....',
      '.........kmk.....gGGg...',
      '.........kmk......gg....',
      '....ggggggggggggggggg...',
      '.........kgk............',
      '....gg...kgk....gg......',
      '...gGGg..kgk...gGGg.....',
      '..gGGGGg.kgk..gGGGGg....',
      '...gggg..kgk...gggg.....',
      '.........kgk............',
      '........kggGk...........',
      '.......kgggggk..........',
      '......kkkkkkkkk.........',
    ],
    ov: [],
  },
  boss_orcamento_secreto: {
    // arquivo sigiloso com gaveta aberta e fantasma saindo
    p: { k: K, c: '#6b6b74', d: '#4a4a52', s: '#8a8f98', w: '#eef0f4', e: '#3a3f4a', r: '#d42a2a' },
    rows: [
      '...........www..........',
      '..........wwwww.........',
      '..........wewew.........',
      '..........wwwww.........',
      '...........ww.w.........',
      '..kkkkkkkkkkkkkkkkkkk...',
      '..kccccccccccccccccck...',
      '..kcdddddddddddddddck...',
      '..kcc sssssssssssscck...',
      '..kccccccccccccccccck...',
      '..kcrrrcccccccccrrrck...',
      '..kcdddddddddddddddck...',
      '..kccccccsssscccccck....',
      '.kddddddddddddddddddk...',
      '.kdcccccccccccccccccdk..',
      '..kccccccssssscccccck...',
      '..kcdddddddddddddddck...',
      '..kkkkkkkkkkkkkkkkkkk...',
    ],
    ov: [],
  },
  boss_lula_planalto: {
    // Palácio do Planalto (laje + colunas curvas + rampa)
    p: { k: K, w: '#f0f0f0', d: '#c9c9c9', b: '#8a8f98', g: '#3a5a8f', r: '#d42a2a' },
    rows: [
      '.........rr.............',
      '.........rr.............',
      '..........k.............',
      '..kkkkkkkkkkkkkkkkkkkk..',
      '.kwwwwwwwwwwwwwwwwwwwwk.',
      '..kkkkkkkkkkkkkkkkkkkk..',
      '...kwk...kwk...kwk......',
      '..kwwk..kwwk..kwwk......',
      '..kwk...kwk...kwk.......',
      '.kwwk..kwwk..kwwk.......',
      '.kwk...kwk...kwk........',
      '..kkkkkkkkkkkkkkkkkkkk..',
      '.kwwwwwwwwwwwwwwwwwwwwk.',
      '.kwggggggggggggggggggwk.',
      '.kwggggggggggggggggggwk.',
      '.kwwwwwwwwwwwwwwwwwwwwk.',
      'kddddddddddddddddddddddk',
      '.kkkkkkkkkkkkkkkkkkkkkk.',
    ],
    ov: [],
  },
  boss_oito_jan_invasao: {
    // vidraça histórica estilhaçada + fumaça
    p: { k: K, g: '#7fd4ff', G: '#4a90c9', w: '#f0f0f0', d: '#c9c9c9', s: '#6b6b74', y: '#f7d117' },
    rows: [
      '....ss......ss..........',
      '...ssss....ssss.........',
      '..kkkkkkkkkkkkkkkkkkkk..',
      '..kwwwwwwwwwwwwwwwwwwk..',
      '..kwggggggGkgggggggggk..',
      '..kwgggggGkkGggggggggk..',
      '..kwggGGGkkkkGGGggggk...',
      '..kwgggggkk.kkgggggggk..',
      '..kwggggGk...kGggggggk..',
      '..kwggGGk.....kGGggggk..',
      '..kwgggk.......kgggggk..',
      '..kwgggGk.....kGgggggk..',
      '..kwggggGkkkkkGggggggk..',
      '..kwgggggggkggggggggk...',
      '..kwwwwwwwwwwwwwwwwwwk..',
      '..kddddddddddddddddddk..',
      '..kyk...kyk...kyk..kyk..',
      '..kkkkkkkkkkkkkkkkkkkk..',
    ],
    ov: [],
  },

  // ======================= PROJÉTEIS / ITENS =======================
  proj_dove: {
    p: { k: K, w: WHITE, d: '#c9cdd6', o: '#f0a030' },
    rows: [
      '....kk....',
      '...kwwk...',
      '..kwwwwko.',
      '.kwwwwwwk.',
      'kwdwwwwk..',
      '.kwwwwk...',
      '..kwwwwk..',
      '...kk.kk..',
    ],
    ov: [],
  },
  proj_fake: {
    p: { k: K, o: '#ff7b3b', d: '#c9541f', w: WHITE, y: '#f7d117' },
    rows: [
      '.......ky.',
      '....kkkoy.',
      '.kkkooookyy',
      'kwooooook.y',
      'kwooooook.y',
      '.kkkooook.y',
      '....kkkoy..',
      '....kdk.y..',
      '....kkk....',
    ],
    ov: [],
  },
  proj_moto: {
    p: { k: K, g: '#1e9e4e', d: '#157a3b', s: '#8a8f98', y: '#f7d117', r: '#ff4433' },
    rows: [
      '......kkk.......',
      '.....kggk.......',
      '..kkkgggkkk.....',
      '.kggggggggggk...',
      'kygggggggggggky.',
      '.kskk.....kksk..',
      'kssssk...kssssk.',
      'ksksks...ksksks.',
      '.kssk.....kssk..',
      '..kk.......kk...',
    ],
    ov: [],
  },
  proj_zap: {
    p: { k: K, g: '#25d366', d: '#128c4a', w: WHITE },
    rows: [
      '.kkkkkkkk.',
      'kggggggggk',
      'kgwgwgwggk',
      'kggggggggk',
      'kgwgwwgggk',
      'kggggggggk',
      '.kkkkdkkk.',
      '....kdk...',
      '....kk....',
    ],
    ov: [],
  },
  proj_cachaca: {
    // garrafinha de 51 (fallback procedural do sprite desenhado em /assets/fx)
    p: { k: K, y: '#e8b13d', d: '#b8862a', w: '#f4f4f4', r: '#c1272d' },
    rows: [
      '...kk...',
      '...krk..',
      '...kyk..',
      '...kyk..',
      '..kyyk..',
      '.kyyyyk.',
      '.kywwyk.',
      '.kywwyk.',
      '.kyyyyk.',
      '.kyddyk.',
      '..kkkk..',
    ],
    ov: [],
  },
  proj_pao: {
    // pão com mortadela em voo (#121): casca dourada, recheio rosa
    p: { k: K, b: '#e0a35c', d: '#b8814a', m: '#e8788c', w: '#f7e7c8' },
    rows: [
      '..kkkkkk..',
      '.kbbbbbbk.',
      'kbwbbwbbbk',
      'kbbbbbbbbk',
      'kmmmmmmmmk',
      'kmbmbmbmbk',
      '.kbdbbdbk.',
      '..kkkkkk..',
    ],
    ov: [],
  },
  proj_vento: {
    // rajada do Estoque de Vento (#121): espiral branco-azulada, sem contorno
    // escuro — vento é quase invisível, como a nova matriz econômica
    p: { k: '#9fc8dd', w: '#e8f4fa', b: '#cfe8f4' },
    rows: [
      '...kkkk...',
      '..kwwwwk..',
      '.kwbbbwwk.',
      '..kkbwwbk.',
      '...kbwwbk.',
      '..kbwwbk..',
      '.kbwwbk...',
      '..kkkk....',
    ],
    ov: [],
  },
  proj_l: {
    // mãozinha fazendo o L (#121): indicador pra cima, polegar aberto
    p: { k: K, s: '#e0ac69', d: '#c68642' },
    rows: [
      '..kk......',
      '.kssk.....',
      '.kssk.....',
      '.kssk.....',
      '.kssskkkk.',
      '.kssssssk.',
      '.kssssssk.',
      '.kdssssdk.',
      '..kssssk..',
      '...kkkk...',
    ],
    ov: [],
  },
  proj_gavel: {
    p: { k: K, m: '#7a5230', d: '#5a3c22' },
    rows: [
      '..kkkkkk..',
      '.kmmmmmmk.',
      '.kmddddmk.',
      '.kmmmmmmk.',
      '..kkmmkk..',
      '....kmk...',
      '....kmk...',
      '....kdk...',
      '....kkk...',
    ],
    ov: [],
  },
  proj_pan: {
    p: { k: K, p: '#4a4a56', d: '#33333d', w: '#b0692f' },
    rows: [
      '.kkkkkk...',
      'kppppppk..',
      'kpddddpkww',
      'kppppppkw.',
      '.kkkkkk...',
    ],
    ov: [],
  },
  proj_carteira: {
    // CTPS azul voadora (assinada na testa)
    p: { k: K, b: '#1d4e9e', d: '#153a78', w: WHITE, y: '#d4af37' },
    rows: [
      '.kkkkkkkk.',
      'kbbbbbbbbk',
      'kbybbybbbk',
      'kbbbbbbbbk',
      'kbwwwwwbbk',
      'kbwwwbbbbk',
      'kbbbbbbbbk',
      'kbddddddbk',
      '.kkkkkkkk.',
    ],
    ov: [],
  },
  proj_cruz: {
    // cruz dourada de arremesso litúrgico
    p: { k: K, y: '#d4af37', d: '#a8862a', w: '#f0e6d2' },
    rows: [
      '...kk...',
      '..kyyk..',
      '..kyyk..',
      'kkyywykk',
      'kyywwyyk',
      'kkyyyykk',
      '..kyyk..',
      '..kyyk..',
      '..kydk..',
      '..kkkk..',
    ],
    ov: [],
  },
  proj_vampiro: {
    // vampirinho-morcego da madrugada: asas, presas e topete
    p: { k: K, r: '#6b2d8f', d: '#4a1e63', w: WHITE, s: SKIN, e: '#d42a2a' },
    rows: [
      'k..........k',
      'kk...kk...kk',
      'krk.krrk.krk',
      'krrkkssrkrrk',
      '.krseesrrk..',
      '.krsssssrk..',
      '..ksmwmsk...',
      '..krrrrrk...',
      '...krrrk....',
      '....kkk.....',
    ],
    ov: [],
  },
  proj_cadeira: {
    // cadeira de debate: encosto, assento e pernas de mogno
    p: { k: K, m: '#8a5a2a', d: '#6b4420', w: '#a8743a' },
    rows: [
      '.kkkkkk.',
      '.kmwwmk.',
      '.kmkkmk.',
      '.kmwwmk.',
      '.kmmmmk.',
      'kmmmmmmk',
      'kddddddk',
      '.km..mk.',
      '.kd..dk.',
      '.kk..kk.',
    ],
    ov: [],
  },
  gem_voto: {
    // cédula de voto com check verde
    p: { k: K, w: WHITE, d: '#d5d9e0', g: '#2ecc71' },
    rows: [
      '.kkkkkk.',
      'kwwwwwwk',
      'kwddddwk',
      'kwwwwgwk',
      'kwgwwgwk',
      'kwwggwwk',
      'kwwwwwwk',
      '.kkkkkk.',
    ],
    ov: [],
  },
  mala: {
    // mala de dinheiro fechada: couro marrom, fecho dourado, notas escapando
    p: { k: K, m: '#7a5230', d: '#5a3c22', y: '#f7d117', g: '#2ecc71', G: '#1a9e4a' },
    rows: [
      '......kkkk......',
      '.....kddddk.....',
      '.gG.kkkkkkkk.Gg.',
      '.kmmmmmmmmmmmmk.',
      'kmddddddddddddmk',
      'kmmmmmkyykmmmmmk',
      'kmmmmmkyykmmmmmk',
      'kmmmmmmkkmmmmmmk',
      'kmdddddddddddmmk',
      'kmmmmmmmmmmmmmmk',
      '.kkkkkkkkkkkkkk.',
    ],
    ov: [],
  },
  gem_voto_big: {
    // urna dourada: voto graúdo de boss
    p: { k: K, y: '#f7d117', d: '#c9a812', w: WHITE, b: '#8a6b12' },
    rows: [
      '.kkkkkkkk.',
      'kyyyyyyyyk',
      'kykkkkkkyk',
      'kyywwwyyyk',
      'kyyyyyyyyk',
      'kydddddyyk',
      'kyyyyyyyyk',
      'kybbbbbbyk',
      '.kkkkkkkk.',
    ],
    ov: [],
  },
  maco_caixa2: {
    // maço de Caixa 2 (#90/#94): notas verdes amarradas com a cinta dourada
    p: { k: K, g: '#2ecc71', G: '#1a9e4a', d: '#0f6e33', y: '#f7d117', w: '#d9f7e4' },
    rows: [
      '.kkkkkkkk.',
      'kggggggggk',
      'kgGwwwwGgk',
      'kgwkyykwgk',
      'kGwkyykwGk',
      'kgwkyykwgk',
      'kgGwwwwGgk',
      'kdddddddgk',
      '.kkkkkkkk.',
    ],
    ov: [],
  },

  // ======================= CENÁRIO =======================
  prop_palmeira: {
    p: { k: '#0e2416', g: '#1e7a3e', G: '#2a9e52', t: '#5a4632', d: '#3f3123' },
    rows: [
      '....GGg....GGg....',
      '..GGgggG.GgggGG...',
      '.Gg..gggGggg..gG..',
      'Gg..GGgggggGG..gG.',
      '...Gg..ttt..gG....',
      '..g...kttk...g....',
      '......kttk........',
      '......kdtk........',
      '.....ktttk........',
      '.....ktdtk........',
      '.....kttdk........',
      '....kttttk........',
      '....ktdttk........',
      '...kkkkkkkk.......',
    ],
    ov: [],
  },
  prop_congresso: {
    // torres + cúpulas do Congresso
    p: { k: '#10151d', w: '#3a4456', d: '#2b3342', l: '#4a5a74' },
    rows: [
      '.....ww..ww.........',
      '.....ww..ww.........',
      '.....ww..ww.........',
      '.....ww..ww.........',
      '.....ww..ww....lll..',
      '.....ww..ww...lwwwl.',
      '.....ww..ww..lwwwwwl',
      '.lll.ww..ww.........',
      'lwwwl.ww.ww.........',
      'wwwwwwwwwwwwwwwwwwww',
      'dddddddddddddddddddd',
    ],
    ov: [],
  },
  prop_cone: {
    p: { k: '#1a1108', o: '#e07020', w: '#f0e6d2', d: '#b05518' },
    rows: [
      '....kk....',
      '...kook...',
      '...kook...',
      '..kwwwwk..',
      '..koodok..',
      '.kwwwwwwk.',
      '.koooodok.',
      'kkkkkkkkkk',
    ],
    ov: [],
  },
  prop_outdoor: {
    // outdoor eleitoral abandonado
    p: { k: '#10151d', w: '#2b3342', d: '#1f2530', r: '#7a2a2a', y: '#8f7a2a', p: '#3a4456' },
    rows: [
      'kkkkkkkkkkkkkkkkkk',
      'kwwwwwwwwwwwwwwwwk',
      'kwrrrwwwwwyyyywwwk',
      'kwrrrwwdwwyyyywwwk',
      'kwwwwwwwwwwwwwwwwk',
      'kwdddwwwwwwddwwwwk',
      'kkkkkkkkkkkkkkkkkk',
      '....kp......pk....',
      '....kp......pk....',
      '....kp......pk....',
    ],
    ov: [],
  },
  prop_congresso_nacional: {
    // Congresso Nacional inteiro: torres gêmeas + concha (Senado) + cúpula (Câmara)
    p: { k: '#10151d', w: '#3d4860', d: '#232b3a', l: '#55648a' },
    rows: [
      '..............ww.ww.............',
      '..............wd.wd.............',
      '..............ww.ww.............',
      '..............wdlwd.............',
      '..............ww.ww.............',
      '..............wd.wd.............',
      '..............ww.ww.............',
      '.llllllllllll.wd.wd......llll...',
      '.lwwwwwwwwwwl.ww.ww....llwwwwll.',
      '..lwwwwwwwwl..wd.wd...lwwwwwwwwl',
      '...llllll.....ww.ww..lwwwwwwwwww',
      'llllllllllllllllllllllllllllllll',
      'wwwwwwwwwwwwwwwwwwwwwwwwwwwwwwww',
      'dddddddddddddddddddddddddddddddd',
    ],
    ov: [],
  },
  prop_planalto: {
    // Palácio do Planalto: laje branca, colunas, vidro e bandeirinha no topo
    p: { k: '#10151d', w: '#707a90', d: '#2b3342', c: '#8a94a8', g: '#2e3b52', F: '#1a7f42', m: '#565c6b' },
    rows: [
      '..FF......................',
      '..m.......................',
      '.wwwwwwwwwwwwwwwwwwwwww...',
      '.dddddddddddddddddddddd...',
      '..cgggcgggcgggcgggcgggc...',
      '..cgggcgggcgggcgggcgggc...',
      '.wwwwwwwwwwwwwwwwwwwwww...',
      '.dddddddddddddddddddddd...',
      '...ww......ww......ww.....',
      '...ww......ww......ww.....',
      '.dddddddddddddddddddddddd.',
    ],
    ov: [],
  },
  prop_stf: {
    // STF: palácio de colunas duplas com a estátua da Justiça na frente
    p: { k: '#10151d', w: '#6b7488', d: '#2b3342', c: '#8a94a8', g: '#2e3b52', s: '#9aa4b8' },
    rows: [
      '.wwwwwwwwwwwwwwwwwwww...',
      '.dddddddddddddddddddd...',
      '..ccgggggccgggggcc......',
      '..ccgggggccgggggcc......',
      '.wwwwwwwwwwwwwwwwww.ss..',
      '.dddddddddddddddddd.ss..',
      '...ww.....ww.....ww.sss.',
      '.ddddddddddddddddddddddd',
    ],
    ov: [],
  },
  prop_catedral: {
    // Catedral de Brasília: coroa de colunas curvas com vitrais
    p: { k: '#10151d', w: '#9aa4b8', g: '#2e5a7a', d: '#2b3342' },
    rows: [
      'w.....w..w.....w',
      '.w....w..w....w.',
      '.w....w..w....w.',
      '..w...w..w...w..',
      '..wg..w..w..gw..',
      '...wg.w..w.gw...',
      '...wggwggwggw...',
      '....wgwggwgw....',
      '....kkkkkkkk....',
      '..dddddddddddd..',
    ],
    ov: [],
  },
  prop_bandeirao: {
    // o bandeirão da Praça dos Três Poderes (100m de pano)
    p: { m: '#8a94a8', G: '#1a7f42', y: '#d9b515', b: '#24406b' },
    rows: [
      '.mGGGGGGGGGG',
      '.mGGGyyyyGGG',
      '.mGGyybbyyGG',
      '.mGGyybbyyGG',
      '.mGGGyyyyGGG',
      '.mGGGGGGGGGG',
      '.m..........',
      '.m..........',
      '.m..........',
      '.m..........',
      '.m..........',
      '.m..........',
      '.m..........',
      '.m..........',
      '.m..........',
      '.m..........',
      '.m..........',
      '.m..........',
      'mmm.........',
    ],
    ov: [],
  },
  prop_ipe: {
    // ipê-amarelo florido (o único consenso nacional)
    p: { y: '#c9a812', Y: '#e8c93a', t: '#4a3826', d: '#33261a', k: '#1a1108' },
    rows: [
      '...yyyyyy....',
      '..yyyyyyyyy..',
      '.yyyYYyyYYyy.',
      '.yyYYyyyyYYy.',
      '..yyyyyyyyy..',
      '...yy.tt.y...',
      '......tt.....',
      '.....ttd.....',
      '.....dtt.....',
      '....ttt......',
      '....ttd......',
      '...kkkkk.....',
    ],
    ov: [],
  },
  prop_poste: {
    // poste da iluminação pública (quando não está apagado)
    p: { p: '#565c6b', y: '#e8d27a' },
    rows: [
      '.ppp....',
      'pyyyp...',
      '.ppp....',
      '..p.....',
      '..p.....',
      '..p.....',
      '..p.....',
      '..p.....',
      '..p.....',
      '..p.....',
      '..p.....',
      '..p.....',
      '..p.....',
      '.ppp....',
    ],
    ov: [],
  },
  prop_banca: {
    // banca de jornal (vende mais bandeirinha que jornal)
    p: { k: '#10151d', r: '#8f3a3a', w: '#b8b8c0', b: '#3a4456', d: '#2b3342' },
    rows: [
      '.kkkkkkkkkkkk.',
      'krwrwrwrwrwrwk',
      'kbbbbbbbbbbbbk',
      'kbwwwwbbwwwbbk',
      'kbwdwdbbwdwbbk',
      'kbbbbbbbbbbbbk',
      '.kkkkkkkkkkkk.',
    ],
    ov: [],
  },
  prop_barraca: {
    // barraca de acampamento em frente ao QG (pauta indefinida)
    p: { k: '#0e2416', g: '#2a6b3a', y: '#c9a812', d: '#14381f' },
    rows: [
      '......kk......',
      '.....kggk.....',
      '....kggggk....',
      '...kggyyggk...',
      '..kggyggyggk..',
      '.kggyggggyggk.',
      'kgggggkddkgggk',
      'kkkkkkkkkkkkkk',
    ],
    ov: [],
  },
  prop_carrinho: {
    // carrinho de pipoca (economia real da Esplanada)
    p: { k: '#10151d', r: '#8f3a3a', w: '#b8b8c0', y: '#d9c26a', d: '#2b3342' },
    rows: [
      '..kkkkkkkk..',
      '.krwrwrwrwk.',
      '..k......k..',
      '..kwwwwwwk..',
      '..kwyywdwk..',
      '..kwwwwwwk..',
      '..kkkkkkkk..',
      '...kk..kk...',
    ],
    ov: [],
  },
  prop_banco: {
    // banco de praça (dormitório oficial de assessor)
    p: { m: '#6b4a2e', d: '#4a3220', k: '#2b2016' },
    rows: [
      '.mmmmmmmmmm.',
      '.dddddddddd.',
      '.mmmmmmmmmm.',
      '.k........k.',
      '.k........k.',
    ],
    ov: [],
  },
  prop_arbusto: {
    // arbusto do cerrado aparado pela zeladoria
    p: { g: '#1e5a30', G: '#2a7a42', k: '#0e2416' },
    rows: [
      '..gggggg..',
      '.gggGGggg.',
      'ggGGggGGgg',
      'gggggggggg',
      '.kkkkkkkk.',
    ],
    ov: [],
  },
  deco_tufo: {
    // tufo de capim pra borda dos gramados
    p: { g: '#2a6b3a', G: '#3a8a4c', d: '#1e5230' },
    rows: [
      'G..g..G',
      'gG.g.Gg',
      '.gdgdg.',
      '..dgd..',
    ],
    ov: [],
  },
  deco_tufo2: {
    p: { g: '#2a6b3a', G: '#3a8a4c', d: '#1e5230' },
    rows: [
      '.g..G.',
      'Gg.gG.',
      '.dgdg.',
      '..dg..',
    ],
    ov: [],
  },
  deco_santinho_a: santinho('#c1272d'), // vermelho
  deco_santinho_b: santinho('#20304a'), // azul-marinho
  deco_santinho_c: santinho('#1e9e4e'), // verde
} satisfies Record<string, SpriteDef>;

export type SpriteId = keyof typeof SPRITES;

export const SPRITE_IDS: string[] = Object.keys(SPRITES);

// Sprite procedural de uma skin (issue #24): convenção '<charId>_<skinId>'.
// Skin padrão (ou sem variante desenhada) cai no sprite do próprio personagem.
export function skinSpriteId(charId: string, skinId: string): string {
  if (!skinId || skinId === DEFAULT_SKIN) return charId;
  const key = `${charId}_${skinId}`;
  return key in SPRITES ? key : charId;
}

// Mapeia tipo de inimigo/boss (shared/enemies) -> sprite
export const ENEMY_SPRITE: Record<string, string> = {
  urna_possuida: 'urna_possuida',
  santinho_voador: 'santinho_voador',
  titulo_zumbi: 'titulo_zumbi',
  biometria_fantasma: 'biometria_fantasma',
  robo_fake_news: 'robo_fake_news',
  carro_de_som: 'carro_de_som',
  mala_de_dinheiro: 'mala_de_dinheiro',
  pesquisa_maldita: 'pesquisa_maldita',
  drone_fiscal: 'drone_fiscal',
  trio_eletrico: 'trio_eletrico',
  live_19h: 'boss_live_19h',
  mensalao2: 'boss_mensalao2',
  impeachment_dilma: 'boss_impeachment_dilma',
  cpi_covid: 'boss_cpi_covid',
  stf_bolso: 'boss_stf_bolso',
  orcamento_secreto: 'boss_orcamento_secreto',
  lula_planalto: 'boss_lula_planalto',
  oito_jan_invasao: 'boss_oito_jan_invasao',
};

export const PROJ_SPRITE: Record<string, string> = {
  dove: 'proj_dove', fake: 'proj_fake', moto: 'proj_moto',
  zap: 'proj_zap', gavel: 'proj_gavel', pan: 'proj_pan',
  cachaca: 'proj_cachaca', carteira: 'proj_carteira',
  cruz: 'proj_cruz', vampiro: 'proj_vampiro', cadeira: 'proj_cadeira',
  pao: 'proj_pao', vento: 'proj_vento', faz_l: 'proj_l',  // issue #121
};

// ---------------------------------------------------------------------------
// SpriteFactory: grade -> canvas (1px = 1 célula) com overlays por cima,
// textura PIXI com NEAREST (pixel crocante, sem blur) e PNG ampliado para o
// DOM (cards de personagem, telas). Tudo com cache interno.
// ---------------------------------------------------------------------------
export class SpriteFactory {
  private readonly canvasCache = new Map<string, HTMLCanvasElement>();
  private readonly texCache = new Map<string, Texture>();
  private readonly dataURLCache = new Map<string, string>();
  private atlasBuilt = false;

  // grade -> canvas (1px = 1 célula) com overlays por cima
  canvas(id: string): HTMLCanvasElement {
    const cached = this.canvasCache.get(id);
    if (cached) return cached;
    const def: SpriteDef | undefined = (SPRITES as Record<string, SpriteDef>)[id];
    if (!def) throw new Error(`sprite desconhecido: ${id}`);
    const w = Math.max(...def.rows.map((r) => r.length));
    const h = def.rows.length;
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const g = canvas.getContext('2d')!;
    def.rows.forEach((row, y) => {
      [...row].forEach((ch, x) => {
        const c = def.p[ch];
        if (c) { g.fillStyle = c; g.fillRect(x, y, 1, 1); }
      });
    });
    for (const [x, y, rw, rh, cor] of def.ov ?? []) {
      g.fillStyle = cor;
      g.fillRect(x, y, rw, rh);
    }
    this.canvasCache.set(id, canvas);
    return canvas;
  }

  // Todos os sprites num ÚNICO BaseTexture (atlas): sprites da mesma base
  // entram no mesmo batch do PixiJS (poucos draw calls). Antes era um
  // BaseTexture por sprite — dezenas de texturas quebrando o batcher com
  // troca de textura na GPU a cada handful de sprites (issue #102).
  private buildAtlas(): void {
    if (this.atlasBuilt) return;
    this.atlasBuilt = true;
    const PAD = 2;    // respiro entre vizinhos: NEAREST não sangra na borda
    const W = 1024;   // largura fixa; a altura cresce por prateleiras
    // Shelf packing: mais altos primeiro = prateleiras justas
    const entries = Object.keys(SPRITES)
      .map((id) => ({ id, c: this.canvas(id) }))
      .sort((a, b) => b.c.height - a.c.height);
    const spot = new Map<string, { x: number; y: number }>();
    let x = PAD, y = PAD, rowH = 0;
    for (const { id, c } of entries) {
      if (x + c.width + PAD > W) { x = PAD; y += rowH + PAD; rowH = 0; }
      spot.set(id, { x, y });
      rowH = Math.max(rowH, c.height);
      x += c.width + PAD;
    }
    const atlas = document.createElement('canvas');
    atlas.width = W;
    atlas.height = y + rowH + PAD;
    const g = atlas.getContext('2d')!;
    const base = new PIXI.BaseTexture(atlas, { scaleMode: PIXI.SCALE_MODES.NEAREST });
    for (const { id, c } of entries) {
      const p = spot.get(id)!;
      g.drawImage(c, p.x, p.y);
      this.texCache.set(id, new PIXI.Texture(base, new PIXI.Rectangle(p.x, p.y, c.width, c.height)));
    }
  }

  // Textura PIXI com NEAREST (pixel crocante, sem blur) — frame do atlas único
  tex(id: string): Texture {
    this.buildAtlas();
    const tex = this.texCache.get(id);
    if (!tex) throw new Error(`sprite desconhecido: ${id}`);
    return tex;
  }

  // PNG ampliado para uso no DOM (cards de personagem, telas) — memoizado
  // porque toDataURL é um encode PNG síncrono caro
  dataURL(id: string, scale = 4): string {
    const key = `${id}:${scale}`;
    const cached = this.dataURLCache.get(key);
    if (cached) return cached;
    const src = this.canvas(id);
    const c = document.createElement('canvas');
    c.width = src.width * scale; c.height = src.height * scale;
    const g = c.getContext('2d')!;
    g.imageSmoothingEnabled = false;
    g.drawImage(src, 0, 0, c.width, c.height);
    const url = c.toDataURL();
    this.dataURLCache.set(key, url);
    return url;
  }
}

// Singleton compartilhado por todo o cliente
export const spriteFactory = new SpriteFactory();
